export type ClinicalSummaryReviewOrigin = 'service' | 'clinician'

export type ClinicalSummaryCategoryOwner = 'service' | 'clinician'

export type ClinicalSummaryReviewState = 'active' | 'not-in-summary' | 'needs-input' | 'removed'

/** provider-api rejects a review term label longer than this. */
export const CLINICAL_SUMMARY_LABEL_MAX_LENGTH = 256

export interface ClinicalSummaryCitation {
  begin: number
  end: number
}

export interface ClinicalSummaryTermReview {
  origin: ClinicalSummaryReviewOrigin
  categoryOwner: ClinicalSummaryCategoryOwner
  reviewState: ClinicalSummaryReviewState
  citations: ClinicalSummaryCitation[]
  /** Display name of a removed term, which is no longer in the clinical diagnosis to carry it. */
  label?: string
}

/**
 * Persisted review metadata is keyed by normalized HPO code. Citation quotes
 * are intentionally excluded so the summary remains the only stored text.
 */
export interface ClinicalSummaryReview {
  schemaVersion: 1
  writerMode: 'merged-review-v1'
  summaryFingerprint: string
  terms: Record<string, ClinicalSummaryTermReview>
  tombstones: string[]
}

/** SHA-256 of the exact UTF-8 summary, matching provider-api validation. */
export function clinicalSummaryFingerprint(value: string): string {
  const words: number[] = []
  const bytes = new TextEncoder().encode(value)
  const bitLength = bytes.length * 8
  for (const byte of bytes) words.push(byte)
  words.push(0x80)
  while (words.length % 64 !== 56) words.push(0)
  for (let i = 7; i >= 0; i -= 1) words.push(i < 4 ? (bitLength >>> (i * 8)) & 0xff : 0)

  const h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]
  // The 64 round constants of FIPS 180-4 section 4.2.2: the first 32 bits of the
  // fractional part of the cube root of each of the first 64 primes (2 through 311).
  // Derived rather than transcribed so the table cannot drift, and must byte-match
  // the server's digest, so k[0] is 0x428a2f98 and k[63] is 0xc67178f2.
  const k = Array.from({ length: 64 }, (_, i) => {
    const prime = nthPrime(i)
    return Math.floor((Math.cbrt(prime) % 1) * 0x100000000) >>> 0
  })
  for (let offset = 0; offset < words.length; offset += 64) {
    const w = new Array<number>(64)
    for (let i = 0; i < 16; i += 1) {
      const j = offset + i * 4
      w[i] = ((words[j] << 24) | (words[j + 1] << 16) | (words[j + 2] << 8) | words[j + 3]) >>> 0
    }
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotate(w[i - 15], 7) ^ rotate(w[i - 15], 18) ^ (w[i - 15] >>> 3)
      const s1 = rotate(w[i - 2], 17) ^ rotate(w[i - 2], 19) ^ (w[i - 2] >>> 10)
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0
    }
    const state = [...h]
    for (let i = 0; i < 64; i += 1) {
      const s1 = rotate(state[4], 6) ^ rotate(state[4], 11) ^ rotate(state[4], 25)
      const choice = (state[4] & state[5]) ^ (~state[4] & state[6])
      const t1 = (state[7] + s1 + choice + k[i] + w[i]) >>> 0
      const s0 = rotate(state[0], 2) ^ rotate(state[0], 13) ^ rotate(state[0], 22)
      const majority = (state[0] & state[1]) ^ (state[0] & state[2]) ^ (state[1] & state[2])
      const t2 = (s0 + majority) >>> 0
      state.unshift((t1 + t2) >>> 0)
      state[4] = (state[4] + t1) >>> 0
      state.pop()
    }
    for (let i = 0; i < 8; i += 1) h[i] = (h[i] + state[i]) >>> 0
  }
  return h.map((word) => word.toString(16).padStart(8, '0')).join('')
}

function rotate(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits))
}

/** Returns the prime at a zero-based index: `nthPrime(0)` is 2, `nthPrime(63)` is 311. */
function nthPrime(index: number): number {
  let candidate = 2
  for (let found = 0; ; candidate += 1) {
    let prime = true
    for (let divisor = 2; divisor * divisor <= candidate; divisor += 1) {
      if (candidate % divisor === 0) {
        prime = false
        break
      }
    }
    if (prime && found++ === index) return candidate
  }
}
